
import {
  Component, Watch, PropSync, Prop, Mixins,
} from 'vue-property-decorator';
import {
  InProgress,
  IntegrationsResponse,
  OrderInfo,
  StoreForImport,
} from '@/features/shipment/types';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { getIntegrationsForImportData } from '@/features/shipment/services/shipment.services';
import { EcommerceSettings } from '@/features/ecommerce/types';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { scrollToInPage, showSuccessAlertDialog } from '@/features/common/helpers';
import OrdersListing from '@/features/shipment/components/createShipment/OrdersListing.vue';

@Component({
  components: {
    OrdersListing,
  },
})
export default class Import extends Mixins(ResponsivenessMixin) {
  @PropSync('showImport', { required: true }) syncedShow!: boolean;

  @PropSync('alwaysShow', { required: true }) syncedAlwaysShow!: boolean;

  @Prop({ required: true }) ecommercePrefs!: EcommerceSettings;

  private showOops = false;

  private importErrorResponse: {
    errorMessage: string,
    redirectUrl?: string | null,
    isExternalLink?: boolean,
  } = {
    errorMessage: '',
    redirectUrl: null,
    isExternalLink: false,
  }

  private store: StoreForImport = {
    id: 0,
    platformType: 'Undefined',
    shopDetails: {
      name: '',
      url: '',
    },
    allowedOptions: {
      fulfillOrder: false,
      storeNotification: false,
    },
    uniqueOrderId: '',
  };

  private orderId = '';

  private isLoading = false;

  private isFormChanged = false;

  private orderTab: 'manual' | 'listing' = 'manual';

  get stores(): Array<StoreForImport> {
    return this.$store.getters.getStoresForImport;
  }

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  @Watch('stores.length', { immediate: true })
  private populateStores(newValue: number) {
    if (!newValue) return;

    const { ecommerce } = this.inProgressData;
    const isInProgressStoreAvailable = ecommerce
      && ecommerce.platformType
      && ecommerce.connectionId;

    this.loadDefaultStore(!!isInProgressStoreAvailable);
  }

  @Watch('isInProgressData', { immediate: true })
  private replaceStore(newValue: number): void {
    if (!newValue) return;

    const { ecommerce } = this.inProgressData;
    if (ecommerce && ecommerce.platformType && ecommerce.connectionId) {
      this.store = {
        id: ecommerce.connectionId,
        platformType: ecommerce.platformType,
        shopDetails: ecommerce.shopDetails,
        allowedOptions: {
          fulfillOrder: ecommerce.fulfillOrder,
          storeNotification: ecommerce.storeNotification,
        },
        uniqueOrderId: ecommerce.uniqueOrderId,
      };
      this.orderId = ecommerce.orderId;
    }
  }

  mounted(): void {
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
    eventBus.$on(eventNames.IMPORT_ORDER_FROM_STORE, this.importOrder);
    eventBus.$on(eventNames.CLEAR_IMPORT_ORDER_FROM, this.clearImportOrderFormAndSave);
  }

  // eslint-disable-next-line class-methods-use-this
  destroyed(): void {
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
    eventBus.$off(eventNames.IMPORT_ORDER_FROM_STORE);
    eventBus.$off(eventNames.CLEAR_IMPORT_ORDER_FROM);
  }

  private loadDefaultStore(isInProgressStoreAvailable: boolean) {
    if (!this.ecommercePrefs) return;

    const { defaultStore } = this.ecommercePrefs;

    if (defaultStore && defaultStore.id && !isInProgressStoreAvailable) {
      const userStorePref = this.stores.find(
        (store) => store.id === defaultStore.id,
      );
      if (userStorePref) {
        this.store = {
          ...userStorePref,
          allowedOptions: {
            fulfillOrder: this.ecommercePrefs.fulfillOrder,
            storeNotification: this.ecommercePrefs.storeNotification,
          },
        };
      }
    }
  }

  private selectOrderFromListing(item: OrderInfo) {
    if (!item || !item.order) return;
    this.orderId = item.order;
    this.orderTab = 'manual';
    this.importOrder(false, false);
  }

  private async importOrder(isOnlyUniqueOrderId = false, isContinueShipment = false) {
    if (!this.orderId || !this.orderId.trim()) return;

    this.isLoading = true;

    const integrationResponse = await getIntegrationsForImportData(
      this.store?.id as number,
      this.orderId,
    );

    if (integrationResponse?.successful) {
      this.setInProgressDataFromImport(integrationResponse, isOnlyUniqueOrderId);
      this.isLoading = false;

      showSuccessAlertDialog(
        this.$t('shipment.alertDialog.importSuccess') as string,
      );

      if (isContinueShipment) {
        this.$emit('submit');
      }

      return;
    }

    let errorMessage = '';
    let isStoreRedirect = false;
    let isExternalLink = false;

    if (!integrationResponse?.successful) {
      const initials = this.$t('labels.importOrderError') as string;

      let message = integrationResponse?.message;

      try {
        const maybeJson = JSON.parse(message || '');
        if (
          typeof maybeJson === 'object'
          && maybeJson !== null
          && typeof maybeJson.return_message === 'string'
        ) {
          message = maybeJson.return_message;
        }
      } catch (e) {
        message = integrationResponse?.message;
      }

      errorMessage = `${initials}<br><br>${message}`;

      if (integrationResponse.errorType === 'ShopifyMissingAccessScopesException') {
        errorMessage = message || '';
        isExternalLink = true;
      }

      if (integrationResponse.redirectUrl) {
        isStoreRedirect = true;
      }
    }

    if (integrationResponse?.numberOfOrdersFound && integrationResponse.numberOfOrdersFound > 1) {
      errorMessage = `${errorMessage}<br><br>${this.$t('labels.importOrderMultiple') as string}`;
    }

    if (integrationResponse?.missingShippingAddress) {
      errorMessage = `${errorMessage}<br><br>${this.$t('labels.importOrderMissing') as string}`;
    }

    if (integrationResponse?.reinstall) {
      errorMessage = `${errorMessage}<br><br>${this.$t('labels.importOrderReinstall') as string}`;
      isStoreRedirect = true;
    }

    if (isStoreRedirect) {
      this.showImportErrorPrompt(
        errorMessage,
        integrationResponse.redirectUrl || '/connect/ecommerce',
        isExternalLink,
      );
      return;
    }

    this.showImportErrorPrompt(errorMessage);
    this.isLoading = false;
  }

  private showImportErrorPrompt(
    errorMessage: string,
    redirectUrl: string | null = null,
    isExternalLink = false,
  ) {
    this.importErrorResponse = {
      errorMessage,
      redirectUrl,
      isExternalLink,
    };
    this.showOops = true;
    this.syncedShow = true;
    scrollToInPage();
  }

  private setInProgressDataFromImport(integration: IntegrationsResponse, isOnlyUniqueOrderId) {
    const data: InProgress = { ...this.inProgressData };

    if (integration.platformType && integration.uniqueOrderId) {
      data.ecommerce = {
        id: this.store.id,
        shopDetails: this.store.shopDetails,
        fulfillOrder: this.store.allowedOptions?.fulfillOrder || false,
        storeNotification: this.store.allowedOptions?.storeNotification || false,
        orderId: this.orderId,
        connectionId: this.store.id,
        platformType: integration.platformType,
        uniqueOrderId: integration.uniqueOrderId,
      };
    }

    if (isOnlyUniqueOrderId) {
      this.$store.commit('setInProgress', data);
      return;
    }

    if (integration.fromAddress) {
      data.fromAddress = integration.fromAddress;
    }
    if (integration.toAddress) {
      data.toAddress = integration.toAddress;
    }
    if (integration.temporaryPackage) {
      data.temporaryPackage = integration.temporaryPackage;
    }
    if (integration.packages) {
      data.packages = integration.packages;
    }
    if (integration.commercialInvoice) {
      data.commercialInvoice = integration.commercialInvoice;
    }

    this.$store.commit('setInProgress', data);
    this.store.uniqueOrderId = integration.uniqueOrderId;
    this.$store.dispatch('syncInProgressData');
  }

  private updateInProgressData(force = false) {
    if (!force && !this.isFormChanged) return;

    this.$store.dispatch('saveInProgressData', {
      key: 'ecommerce',
      value: {
        connectionId: this.store.id,
        fulfillOrder: this.store?.allowedOptions?.fulfillOrder,
        orderId: this.orderId,
        storeNotification: this.store?.allowedOptions?.storeNotification,
        platformType: this.store?.platformType,
        shopDetails: this.store?.shopDetails,
        uniqueOrderId: this.store.uniqueOrderId,
      },
    });
  }

  private clearAllInformation() {
    this.orderId = '';
    this.store = {
      id: 0,
      platformType: 'Undefined',
      shopDetails: {
        name: '',
        url: '',
      },
      allowedOptions: {
        fulfillOrder: false,
        storeNotification: false,
      },
    };
    this.loadDefaultStore(false);
    this.isFormChanged = false;
  }

  private clearImportOrderFormAndSave() {
    this.clearAllInformation();
    this.updateInProgressData(true);
  }

  private handleCancelImport() {
    this.syncedShow = false;
    this.clearImportOrderFormAndSave();
    this.$emit('cancelImport');
  }
}
